import { useState, useEffect, useRef, useCallback } from 'react';
import { imageCache } from '../utils/imageCache';
import { resolveStorageUrl } from '../utils/storageUrl';

/**
 * Custom hook for optimized image loading with caching and lazy loading
 * @param {string} rawSrc - Image source URL
 * @param {string} cacheKey - Unique cache key for this image
 * @param {boolean} lazy - Whether to use lazy loading (default: true)
 * @returns {Object} - Image state and ref
 */
export const useOptimizedImage = (rawSrc, cacheKey, lazy = true) => {
  // Rewrite legacy (old Supabase project) storage URLs to the current host
  const src = resolveStorageUrl(rawSrc);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [cachedImageLoaded, setCachedImageLoaded] = useState(false);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const imageRef = useRef(null);
  const loadTimeoutRef = useRef(null);

  // Get cached URL or cache the new one
  const cachedSrc = src ? imageCache.get(cacheKey, src) : null;

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || !containerRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Stop observing once visible
            if (observerRef.current) {
              observerRef.current.disconnect();
              observerRef.current = null;
            }
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
        threshold: 0.1
      }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [lazy]);

  // Reset and check cached images when src changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setCachedImageLoaded(false);
    
    // Clear any existing timeout
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    
    // Only proceed if we have a valid src
    if (!cachedSrc) return;
    
    // Check if the new image is already cached/complete
    if (imageRef.current && imageRef.current.src === cachedSrc && 
        imageRef.current.complete && imageRef.current.naturalWidth > 0) {
      // Use requestAnimationFrame for reliable timing
      requestAnimationFrame(() => {
        setCachedImageLoaded(true);
        setImageLoaded(true);
      });
    } else if (isVisible) {
      // Set a timeout to detect stuck loading states
      loadTimeoutRef.current = setTimeout(() => {
        if (!imageLoaded && !cachedImageLoaded && !imageError && imageRef.current) {
          // Force a reload attempt by setting error and then resetting
          console.warn('Image loading timeout, attempting recovery:', cachedSrc);
          setImageError(true);
          setTimeout(() => {
            setImageError(false);
            setImageLoaded(false);
            setCachedImageLoaded(false);
          }, 100);
        }
      }, 10000); // 10 second timeout
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- must only re-run (and reset load state) when the source/visibility changes; adding the load-state values would retrigger the reset on every load event
  }, [cachedSrc, isVisible]);

  // Handle image load/error events
  const handleImageLoad = useCallback((event) => {
    // Clear the timeout since image loaded successfully
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    
    setImageLoaded(true);
    setImageError(false);
    setCachedImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    // Clear the timeout since we got a definitive error
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    
    setImageError(true);
    setImageLoaded(false);
    setCachedImageLoaded(false);
  }, []);

  // Combined ref handler that sets both refs
  const setImageRef = useCallback((element) => {
    imageRef.current = element;
    // Check if already loaded when ref is set
    if (element && element.complete && element.naturalWidth > 0 && element.src === cachedSrc) {
      // Use requestAnimationFrame instead of setTimeout for more reliable timing
      requestAnimationFrame(() => {
        setCachedImageLoaded(true);
        setImageLoaded(true);
      });
    }
  }, [cachedSrc]);

  // Combined loading state - image is loaded if either state indicates it
  const actuallyLoaded = imageLoaded || cachedImageLoaded;

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, []);

  return {
    imageLoaded: actuallyLoaded,
    imageError,
    isVisible,
    imgRef: setImageRef,
    containerRef, // Return container ref for lazy loading
    src: cachedSrc,
    onLoad: handleImageLoad,
    onError: handleImageError
  };
};